import React, { useState } from 'react';

import {
    CreateContractDealsInput,
    CreateContractDealsOutput
} from '@amzn/coral_com-amazon-musicpapercontractconfigurationservice';
import { UnknownCoralError } from '@amzn/ihm-js-coral-client';
import Button from '@amzn/meridian/button';
import Text from '@amzn/meridian/text';

import { isEmpty, isEqual } from 'lodash';
import { useHistory } from 'react-router-dom';
import { contractConfigurationClient } from 'src/api/client';
import { ConfirmationModal } from 'src/components/ConfirmationModal';
import { DEFAULT_TOASTER_TIMEOUT } from 'src/constants';
import { STEP_TEXT } from 'src/constants/addDeal';
import { ROUTES } from 'src/constants/routes';
import { AddDealContext } from 'src/contexts/addDealContext';
import { getDefaultRateCardValues } from 'src/contexts/addDealContext';
import { AppContext } from 'src/contexts/appContext';
import { IToast } from 'src/types/IToaster';
import { convertDateStringToDateObj } from 'src/util/dateTimeUtil';

import './AddDealFooter.scss';

export interface IAddDealFooterProps {
    step: number;
    setStep: React.Dispatch<React.SetStateAction<number>>;
}

export const AddDealFooter = ({ step, setStep }: IAddDealFooterProps) => {
    const { setToasts, setTopAlertMsg, userInfo } = React.useContext(AppContext);
    const {
        services,
        selectedVendors,
        rateCardValues,
        isCancelModalOpen,
        setIsCancelModalOpen,
        requireRerun,
        statusToDealsObj,
        specifyDealGridRowData,
        rateCardErrorStates
    } = React.useContext(AddDealContext);

    const history = useHistory();

    const [isSubmitRerunModalOpen, setIsSubmitRerunModalOpen] = useState<boolean>(false);
    const [rerunModalComment, setRerunModalComment] = useState<string>('');

    const buildCreateContractDealsPayload = () => {
        const addedDeals = statusToDealsObj['new'].map((deal) => {
            const dealObj = {
                contractId: deal.contractId,
                entityId: deal.entityId,
                startDate: deal.startDate instanceof Date ? deal.startDate : convertDateStringToDateObj(deal.startDate)
            };
            // @ts-ignore
            if (deal.endDate) dealObj['endDate'] = convertDateStringToDateObj(deal.endDate);

            return dealObj;
        });

        const modifiedDeals = statusToDealsObj['updated'].map((deal) => {
            const dealObj = {
                entityContractId: deal.entityContractId
            };

            // only include startDate if it's modified
            if (deal.startDate && deal.isStartDateUpdated) {
                // @ts-ignore
                dealObj['startDate'] = convertDateStringToDateObj(deal.startDate);
            }

            // only include endDate if it's modified
            if (deal.endDate && !deal.isStartDateUpdated) {
                // @ts-ignore
                dealObj['endDate'] = convertDateStringToDateObj(deal.endDate);
            }

            return dealObj;
        });

        const cancelledDeals = statusToDealsObj['canceled'].map((deal) => deal.entityContractId);

        return {
            addedDeals,
            modifiedDeals,
            cancelledDeals,
            createdBy: userInfo.username
        } as CreateContractDealsInput;
    };

    const handleSubmitDeal = () => {
        if (requireRerun) {
            setIsSubmitRerunModalOpen(true);
        } else {
            handleSubmitWithToast();
        }
    };

    const handleSubmitWithToast = () => {
        const toastMessage = `${
            statusToDealsObj['new'].length ? statusToDealsObj['new'].length + ' deals added. ' : ''
        } ${statusToDealsObj['updated'].length ? statusToDealsObj['updated'].length + ' deals updated. ' : ''} ${
            statusToDealsObj['canceled'].length ? statusToDealsObj['canceled'].length + ' deals canceled.' : ''
        }`;
        const toast: IToast = {
            id: 'add-deal-toast',
            timeout: DEFAULT_TOASTER_TIMEOUT * 2,
            message: toastMessage
        };

        contractConfigurationClient
            .createContractDeals(CreateContractDealsInput.clone(buildCreateContractDealsPayload()))
            .subscribe(
                (response: CreateContractDealsOutput) => {
                    setToasts([toast]);
                    history.push(ROUTES.DEALS.INDEX);
                },
                (error: UnknownCoralError) => {
                    // eslint-disable-next-line no-console
                    console.error('CeateContractDeals Unexpected error', error.receivedException.message);
                    setTopAlertMsg(error.receivedException.message);
                }
            );
    };

    const isSpecifyDealGridValid = () => {
        if (isEqual(rateCardValues, getDefaultRateCardValues(services))) return false;

        for (const service of Object.keys(rateCardErrorStates)) {
            const rateCardErrorState = rateCardErrorStates[service];

            if (rateCardErrorState.isStartDateInError || rateCardErrorState.isEndDateInError) return false;
        }

        for (const row of specifyDealGridRowData) {
            if (row.contractName && !row.startDate) return false;
        }

        return true;
    };

    const onNext = () => {
        setTimeout(() => {
            // Next button handler for Select vendor step
            if (step === 1) {
                if (!isEmpty(selectedVendors)) {
                    setStep(step + 1);
                }
            }
            // Next button handler for Specify deal step
            else {
                // Force all the inputs to blur to guarantee validation is executed
                document.querySelectorAll('input').forEach((element) => {
                    if (element === document.activeElement) {
                        // @ts-ignore
                        return element.blur();
                    }
                });
                if (isSpecifyDealGridValid()) {
                    setStep(step + 1);
                }
            }
        }, 0);
    };

    return (
        <div className="footer">
            <div className="footer-text">
                <Text className="step-number">Step {step} of 3</Text>
                <Text>{STEP_TEXT[step]}</Text>
            </div>
            <div className="footer-button-group">
                <Button type="link" size="medium" onClick={() => setIsCancelModalOpen(true)}>
                    Cancel
                </Button>
                {step !== 1 && (
                    <Button type="tertiary" size="medium" onClick={() => setStep(step - 1)}>
                        Previous
                    </Button>
                )}
                {step !== 3 && (
                    <Button
                        size="medium"
                        disabled={step === 1 ? isEmpty(selectedVendors) : !isSpecifyDealGridValid()}
                        onClick={onNext}
                    >
                        Next
                    </Button>
                )}
                {step === 3 && (
                    <Button size="medium" onClick={() => handleSubmitDeal()}>
                        Yes, submit the deal updates
                    </Button>
                )}
            </div>
            <ConfirmationModal
                isOpen={isCancelModalOpen}
                onClose={() => setIsCancelModalOpen(false)}
                title="Cancel add deals"
                body="Are you sure you want to cancel? Any changes will be lost."
                primaryButtonText="Cancel add deals"
                primaryButtonOnClick={() => {
                    history.push(ROUTES.DEALS.INDEX);
                }}
                secondaryButtonText="Go back"
                secondaryButtonOnClick={() => setIsCancelModalOpen(false)}
            />
            <ConfirmationModal
                isOpen={isSubmitRerunModalOpen}
                onClose={() => setIsSubmitRerunModalOpen(false)}
                title="Submit deal updates"
                body="Adding these deals may require reruns for past accounting periods."
                comments={rerunModalComment}
                showComments={true}
                onCommentsChange={setRerunModalComment}
                primaryButtonText="Yes, submit the deal updates"
                primaryButtonOnClick={handleSubmitWithToast}
                secondaryButtonText="Go back"
                secondaryButtonOnClick={() => setIsSubmitRerunModalOpen(false)}
            />
        </div>
    );
};
